import { useEffect, useState } from "react";

export const TRADING_DAY_START_STORAGE_KEY = "calendar_trading_day_start";

/** Custom event used to broadcast the setting between components mounted in the
 *  same tab. The native `storage` event only fires in *other* tabs, so without
 *  this the calendar and the day-detail modal would drift apart until reload. */
const TRADING_DAY_START_EVENT = "edgely:trading-day-start-changed";

function readStoredHour(): number {
  const v = parseInt(localStorage.getItem(TRADING_DAY_START_STORAGE_KEY) ?? "0", 10);
  if (isNaN(v)) return 0;
  // Guard against a corrupted/out-of-range value: the UI slider is 0..23.
  return Math.min(23, Math.max(0, v));
}

/**
 * Shared "trading day start hour" (UTC) setting.
 *
 * Single source of truth for trade→day attribution. The calendar cells and the
 * day-detail modal MUST use the same value, otherwise a trade in the shifted
 * window lands on one day in the grid and another in the modal, which showed
 * up as a P&L mismatch between a calendar cell and its own detail popup.
 *
 * Persisted in localStorage; changes broadcast to every hook instance in the
 * tab so an open modal stays consistent with the calendar behind it.
 */
export function useTradingDayStart(): [number, (hour: number) => void] {
  const [hour, setHour] = useState<number>(readStoredHour);

  useEffect(() => {
    const sync = () => setHour(readStoredHour());
    // Same tab (our own broadcast) + other tabs (native storage event).
    window.addEventListener(TRADING_DAY_START_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(TRADING_DAY_START_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const update = (next: number) => {
    localStorage.setItem(TRADING_DAY_START_STORAGE_KEY, String(next));
    setHour(next);
    window.dispatchEvent(new Event(TRADING_DAY_START_EVENT));
  };

  return [hour, update];
}
